import { useMemo, useState, useEffect, useCallback } from "react";
import { SmartInspectField } from "./SmartInspectField";
import { SmartInspectPackageSelect } from "./SmartInspectPackageSelect";
import { SmartInspectResults } from "./SmartInspectResults";
import { countryCascadePatch, currencyCascadePatch, findCountryQuestion } from "../lib/smartInspectApi";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, CheckCircle2, Loader2, ClipboardCheck,
} from "lucide-react";

const PACKAGE_KEYS = [
  "selected_package_id", "selected_package_code", "selected_package_name",
  "package_currency", "package_pricing_snapshot",
  "package_version_timestamp",
  "selected_addons", "selected_addons_snapshot",
];

// Country-derived reference fields (not questions themselves) that must persist
// alongside Step 1 whenever a country is chosen (Sprint 8O).
const COUNTRY_DERIVED_KEYS = ["country_code", "pricing_region", "currency_name", "currency_symbol"];

// Sprint 8AA — benchmark-derived defaults computed at country selection (Step 1)
// but living in Step 2. They must persist with Step 1 so they survive the
// Step 1 → Step 2 transition (otherwise the re-hydrate wipes them).
const BENCHMARK_DERIVED_KEYS = ["current_annual_device_cost", "current_annual_storage_it_cost"];

// Step-2 fields that arrive pre-filled with a default (seed or regional). Their
// value is shown in a lighter colour while it still holds the default, and turns
// normal (dark) once the consultant edits it.
const DEFAULT_LIGHT_KEYS = new Set([
  "operating_locations", "operating_days_per_year",
  "visual_inspection_time", "photograph_capture_time", "code_recording_time", "inspector_hourly_cost",
  "report_email_time", "photo_transfer_time", "sap_erp_entry_time",
  "current_annual_storage_it_cost",
]);

// Reusable SmartInspect ROI wizard (public / consultant / admin / demo).
// Props:
//   config: grouped steps [{key,index,title,sections:[{section,questions:[]}]}]
//   assessment: {id, resume_token, answers, current_step, status, preview}
//   onPersist(partialAnswers, {step, complete}) -> Promise<updatedAssessment>
//   readOnly: disable inputs
export default function SmartInspectRoiForm({ config, benchmarks = {}, workingHours = 8, packages = [], assessment, onPersist, onCalculate, readOnly = false }) {
  const [values, setValues] = useState(assessment?.answers || {});
  const [step, setStep] = useState(assessment?.current_step || 1);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(() => new Set());
  const status = assessment?.status;

  useEffect(() => {
    const seeded = { ...(assessment?.answers || {}) };
    // Apply question default_value where the field has no saved value yet.
    config.forEach((s) => (s.sections || []).forEach((sec) => sec.questions.forEach((q) => {
      if ((seeded[q.field_key] === undefined || seeded[q.field_key] === "") && q.default_value !== null && q.default_value !== undefined && q.default_value !== "") {
        seeded[q.field_key] = q.default_value;
      }
    })));
    setValues(seeded);
  }, [assessment?.id, config]);

  const inputSteps = config.filter((s) => s.key !== "step4");
  const currentCfg = config.find((s) => s.index === step);

  // Sprint 8AA — Equipment/Storage benchmark defaults.
  // Annual Device Cost = Regional Device Benchmark × Number of Devices
  // (0 when Device Type = "No Separate Device Cost"). Storage & IT auto-fills
  // from the regional benchmark. Both recalc on region/devices/device-type
  // changes and remain fully overridable by the consultant.
  const NO_DEVICE = "No Separate Device Cost";
  const computeDeviceCost = (region, numDevices, deviceType) => {
    if (deviceType === NO_DEVICE) return 0;
    const bm = benchmarks[region] || {};
    if (bm.device_cost == null) return undefined;
    return +(Number(bm.device_cost) * (Number(numDevices) || 0));
  };

  const setVal = (k, v) => {
    if (DEFAULT_LIGHT_KEYS.has(k)) setTouched((t) => (t.has(k) ? t : new Set(t).add(k)));
    setValues((p) => {
    if (k === "country") {
      const patch = countryCascadePatch(findCountryQuestion(config), v);
      const region = patch.pricing_region;
      const bm = benchmarks[region] || {};
      const dc = computeDeviceCost(region, p.number_of_devices, p.current_device_type);
      if (dc !== undefined) patch.current_annual_device_cost = dc;
      if (bm.storage_it_benchmark != null) patch.current_annual_storage_it_cost = bm.storage_it_benchmark;
      return { ...p, ...patch };
    }
    if (k === "preferred_currency") {
      const patch = currencyCascadePatch(findCountryQuestion(config), v);
      return { ...p, ...patch };
    }
    if (k === "number_of_devices" || k === "current_device_type") {
      const nextDevices = k === "number_of_devices" ? v : p.number_of_devices;
      const nextType = k === "current_device_type" ? v : p.current_device_type;
      const dc = computeDeviceCost(p.pricing_region, nextDevices, nextType);
      const patch = { [k]: v };
      if (dc !== undefined) patch.current_annual_device_cost = dc;
      return { ...p, ...patch };
    }
    return { ...p, [k]: v };
    });
  };
  // Preferred currency identifies the unit of financial fields (no conversion).
  const currency = values.preferred_currency || "USD";

  // Live client-side previews (informational only — NOT the ROI engine).
  const previews = useMemo(() => {
    const n = (k) => Number(values[k] || 0);
    const volume = n("operating_locations") * n("inspections_per_day_per_location") * n("operating_days_per_year");
    // Sprint 9T — section-independent time total: sum every step-2 question
    // measured in minutes (regrouping split the old "Current Inspection Time"
    // section). Value/behaviour unchanged.
    const step2Secs = config.find((s) => s.key === "step2")?.sections || [];
    const timeQs = step2Secs.flatMap((sec) => sec.questions || [])
      .filter((q) => String(q.unit || "").trim().toLowerCase() === "minutes");
    const totalTime = timeQs.reduce((a, q) => a + n(q.field_key), 0);
    return { annual_inspection_volume: volume, current_total_time_per_inspection: totalTime };
  }, [values, config]);

  const stepQuestions = useMemo(() => {
    const qs = [];
    (currentCfg?.sections || []).forEach((sec) => sec.questions.forEach((q) => qs.push(q)));
    return qs;
  }, [currentCfg]);

  const validateStep = useCallback(() => {
    const e = {};
    for (const q of stepQuestions) {
      const v = values[q.field_key];
      const empty = v === undefined || v === null || v === "";
      if (q.required && empty) { e[q.field_key] = `${q.label} is required.`; continue; }
      if (!empty && ["number", "currency", "percentage"].includes(q.input_type)) {
        const num = Number(v);
        if (Number.isNaN(num)) { e[q.field_key] = `${q.label} must be a number.`; continue; }
        if (["number", "currency"].includes(q.input_type) && num < 0) e[q.field_key] = `${q.label} cannot be negative.`;
        if (q.input_type === "percentage" && (num < 0 || num > 100)) e[q.field_key] = `${q.label} must be between 0 and 100.`;
        if (q.min_value != null && num < Number(q.min_value)) e[q.field_key] = `${q.label} must be at least ${q.min_value}.`;
        if (q.max_value != null && num > Number(q.max_value)) e[q.field_key] = `${q.label} must not exceed ${q.max_value}.`;
      }
    }
    setErrors(e);
    if (currentCfg?.key === "step3" && packages.length > 0) {
      if (!values.selected_package_code) { toast.error("Please select a SmartInspect package."); return false; }
    }
    return Object.keys(e).length === 0;
  }, [stepQuestions, values, currentCfg, packages]);

  const persist = async (opts) => {
    const partial = {};
    stepQuestions.forEach((q) => { partial[q.field_key] = values[q.field_key]; });
    if (currentCfg?.key === "step1") {
      COUNTRY_DERIVED_KEYS.forEach((k) => { if (values[k] !== undefined) partial[k] = values[k]; });
      BENCHMARK_DERIVED_KEYS.forEach((k) => { if (values[k] !== undefined) partial[k] = values[k]; });
    }
    if (currentCfg?.key === "step3") {
      PACKAGE_KEYS.forEach((k) => { if (values[k] !== undefined) partial[k] = values[k]; });
    }
    setBusy(true);
    try {
      const updated = await onPersist(partial, opts);
      if (updated?.answers) setValues((prev) => ({ ...prev, ...updated.answers }));
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.errors) setErrors(detail.errors);
      toast.error(detail?.message || detail || "Could not save. Please check your entries.");
      return false;
    } finally { setBusy(false); }
  };

  const goNext = async () => {
    if (!validateStep()) { toast.error("Please correct the highlighted fields."); return; }
    const isLastInput = step === inputSteps.length;
    const ok = await persist({ step: isLastInput ? 4 : step + 1, complete: isLastInput });
    if (ok) setStep(isLastInput ? 4 : step + 1);
  };
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const onRecalculate = async () => { if (onCalculate) await onCalculate(); };

  const totalInput = inputSteps.length;

  const volumePreview = (
    <div data-testid="si-preview-volume" className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3 text-sm text-indigo-800">
      <b>Annual Inspection Volume</b>: {previews.annual_inspection_volume.toLocaleString()} inspections/year
      <span className="text-indigo-400"> = Operating Locations × Inspections Per Day Per Location × Operating Days Per Year</span>
    </div>
  );
  const timePreview = (
    <div data-testid="si-preview-time" className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3 text-sm text-indigo-800">
      <b>Current Total Time Per Inspection</b> (preview): {previews.current_total_time_per_inspection.toLocaleString()} minutes
    </div>
  );

  return (
    <div data-testid="si-roi-form" className="max-w-3xl mx-auto">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6" data-testid="si-progress">
        {config.map((s) => (
          <div key={s.key} className="flex-1">
            <div className={`h-1.5 rounded-full ${s.index < step ? "bg-emerald-500" : s.index === step ? "bg-indigo-600" : "bg-slate-200"}`} />
            <div className={`mt-1 text-[11px] ${s.index === step ? "text-indigo-700 font-semibold" : "text-slate-400"}`}>
              Step {s.index}: {s.title}
            </div>
          </div>
        ))}
      </div>

      {step <= totalInput ? (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-900">{currentCfg?.title}</h2>
          {currentCfg?.key === "step3" && packages.length > 0 ? (
            <SmartInspectPackageSelect
              packages={packages}
              values={values}
              setVal={setVal}
              annualVolume={previews.annual_inspection_volume}
            />
          ) : null}
          {(currentCfg?.sections || []).map((sec) => (
            <div key={sec.section}>
              <div data-testid={`si-section-${sec.section}`} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">{sec.section}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sec.questions.map((q) => {
                    return (
                    <div key={q.field_key} className={q.input_type === "textarea" ? "md:col-span-2" : ""}>
                      <SmartInspectField q={q} value={values[q.field_key]} error={errors[q.field_key]}
                        currency={currency}
                        isDefault={DEFAULT_LIGHT_KEYS.has(q.field_key) && !touched.has(q.field_key)}
                        disabled={readOnly || (q.customer_editable === false && false)}
                        onChange={(v) => setVal(q.field_key, v)} />
                    </div>
                    );
                  })}
                </div>
              </div>
              {sec.section === "Inspection Operations" ? <div className="mt-3">{volumePreview}</div> : null}
              {sec.section === "SAP / ERP Entry" ? <div className="mt-3">{timePreview}</div> : null}
            </div>
          ))}

          <div className="flex items-center justify-between pt-2">
            <Button data-testid="si-prev-btn" variant="outline" onClick={goPrev} disabled={step === 1 || busy}>
              <ChevronLeft size={16} className="mr-1" /> Previous
            </Button>
            <div className="flex gap-2">
              <Button data-testid="si-next-btn" onClick={goNext} disabled={busy || readOnly}>
                {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <ChevronRight size={16} className="mr-1" />}
                {step === totalInput ? "Complete & View Results" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        // -------- STEP 4 : ROI Results --------
        <div data-testid="si-step4">
          <h2 className="text-xl font-bold text-slate-900">SmartInspect ROI Results</h2>
          <p className="text-slate-500 mt-1 mb-5 text-sm">Estimated financial and operational impact based on your inputs and the selected SmartInspect package.</p>
          {assessment?.roi_result ? (
            <SmartInspectResults
              result={assessment.roi_result}
              onEditStep={(n) => setStep(n)}
              onRecalculate={onRecalculate}
              hideZeroSavings
            />
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
              <ClipboardCheck size={40} className="mx-auto text-emerald-500 mb-3" />
              <p className="text-slate-500">Your inputs are saved. Click Recalculate to generate your ROI results.</p>
              <div className="mt-4"><Button data-testid="si-recalculate-empty" onClick={onRecalculate}>Calculate ROI</Button></div>
              <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                <Button data-testid="si-edit-step1" variant="outline" onClick={() => setStep(1)}>Edit Company Information</Button>
                <Button data-testid="si-edit-step2" variant="outline" onClick={() => setStep(2)}>Edit Current Operations</Button>
                <Button data-testid="si-edit-step3" variant="outline" onClick={() => setStep(3)}>Edit SmartInspect Plan</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
